<template>
    <!-- Wrapper allows sub-benefit rows to appear after the parent row -->
    <div>
        <div
            class="flex items-center hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors border-slate-200 dark:border-slate-700 py-5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-slate-200 relative group"
            :class="{
                'border-t': isAGroup,
                'border-b': !isAGroup || isExpanded,
                'cursor-pointer': !benefitItem.isLocked,
                'cursor-default': benefitItem.isLocked,
            }"
            tabindex="0"
            @click="handleRowClick"
        >
            <!-- Tree SVG Connectors for grouped items -->
            <div
                v-if="isAGroup"
                class="absolute bg-white dark:bg-slate-800 w-16 -left-16 -top-0 bottom-0 h-full group-hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40"
            >
                <svg
                    v-if="index === 0"
                    width="12"
                    height="70"
                    viewBox="0 0 12 70"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="absolute -top-2 left-12"
                >
                    <path
                        d="M1.5 5V50Q1.5 63 12 63"
                        stroke="#CBD5E1"
                        stroke-width="1"
                        stroke-linecap="round"
                    />
                </svg>
                <svg
                    v-else
                    width="12"
                    height="110"
                    viewBox="0 0 12 110"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="absolute -top-12 left-12"
                >
                    <path
                        d="M1.5 0V85Q1.5 98 12 98"
                        stroke="#CBD5E1"
                        stroke-width="1"
                        stroke-linecap="round"
                    />
                </svg>
            </div>

            <!-- All Columns (matching header structure) -->
            <template v-for="column in filteredColumns" :key="column.key">
                <!-- Benefit Column -->
                <div
                    v-if="column.key === 'benefit'"
                    class="flex items-center min-w-0"
                    :class="[
                        { 'pl-6': !isAGroup },
                        column.width,
                        { 'opacity-50': benefitItem.isLocked },
                    ]"
                >
                    <!-- BenefitIcon with hidden-state badge overlay (mirrors the
                     My Wallet bottom-corner badge pattern). Badge only renders
                     when the row is visible because "Show hidden benefits" is on. -->
                    <div class="relative shrink-0 mr-4">
                        <BenefitIcon
                            :category="benefitItem?.benefit?.category"
                            :title="benefitItem?.benefit?.benefit_title"
                            :description="
                                benefitItem?.benefit?.benefit_description
                            "
                            svg-class="w-6 h-6 text-slate-600 dark:text-slate-300"
                        />
                        <div
                            v-if="benefitItem.user_override?.is_hidden === true"
                            class="absolute -bottom-1 -right-1"
                        >
                            <!-- MY1-3084: same wording and slashed-eye as a hidden
                                 transaction in My Spending -->
                            <Tooltip
                                text="Benefit hidden from list"
                                yPlacement="top"
                                xPlacement="right"
                                class="custom-tooltip custom-tooltip-left"
                            >
                                <template #trigger>
                                    <div
                                        class="inline-flex items-center justify-center w-4 h-4 rounded-full border-2 border-white bg-slate-800 text-white shadow-lg"
                                    >
                                        <Icon icon="eye-off" class="w-2 h-2" />
                                    </div>
                                </template>
                            </Tooltip>
                        </div>
                    </div>

                    <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                            <!-- Expand/collapse toggle for compound benefits -->
                            <button
                                v-if="hasSubBenefits"
                                class="shrink-0 w-4 h-4 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:text-slate-300 transition-colors"
                                @click.stop="isExpanded = !isExpanded"
                                :aria-label="
                                    isExpanded
                                        ? 'Collapse sub-benefits'
                                        : 'Expand sub-benefits'
                                "
                            >
                                <Icon
                                    icon="chevron-right"
                                    svgClass="w-3 h-3 transition-transform"
                                    :class="{ 'rotate-90': isExpanded }"
                                />
                            </button>
                            <div
                                class="font-medium text-sm line-clamp-1 leading-5 text-left relative flex-1 min-w-0"
                                :class="[
                                    { 'cursor-help': isBenefitTitleTruncated },
                                    benefitItem.is_active === false
                                        ? 'text-slate-400'
                                        : 'text-slate-800 dark:text-slate-100',
                                ]"
                                @mouseenter="showTooltip = true"
                                @mouseleave="showTooltip = false"
                            >
                                {{
                                    benefitItem.benefit.benefit_title ||
                                    'Unknown Benefit'
                                }}

                                <!-- Title truncation tooltip -->
                                <div
                                    v-if="
                                        isBenefitTitleTruncated && showTooltip
                                    "
                                    class="absolute left-0 top-full mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg z-50 pointer-events-none whitespace-normal max-w-xs"
                                >
                                    {{
                                        benefitItem.benefit.benefit_title ||
                                        'Unknown Benefit'
                                    }}
                                </div>
                            </div>
                            <!-- Inactive badge -->
                            <span
                                v-if="benefitItem.is_active === false"
                                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 shrink-0"
                            >
                                Inactive
                            </span>
                            <!-- MY1-3079: auto-tracking provenance — on the title row so it
                                 shows in flat and by-card views alike (wide screens only;
                                 the sidebar carries it everywhere) -->
                            <BenefitAutoTrackingLabel
                                :auto-tracking="benefitItem.auto_tracking"
                                compact
                                class="shrink-0 hidden xl:inline-flex"
                            />
                        </div>

                        <div
                            v-if="viewMode !== 'by_card'"
                            class="font-normal text-slate-800 dark:text-slate-100 text-sm flex items-center gap-2 mt-1"
                        >
                            <div class="shrink-0">
                                <ImageLoader>
                                    <template #image>
                                        <img
                                            :src="`${cdnUrl}/${benefitItem.card.image?.path}/_small/${benefitItem.card.image?.file_name}`"
                                            :alt="benefitItem.card.title"
                                            class="w-8 h-5 rounded-sm"
                                        />
                                    </template>
                                    <template #error>
                                        <div
                                            class="w-8 h-5 bg-slate-100 dark:bg-slate-700 rounded-sm border border-slate-200 dark:border-slate-700 flex items-center justify-center"
                                        >
                                            <svg
                                                class="w-3 h-3 text-slate-400"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                                    clip-rule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                    </template>
                                </ImageLoader>
                            </div>
                            <div class="flex items-center min-w-0">
                                <div class="flex-1 min-w-0 mr-2 relative">
                                    <span
                                        class="block relative overflow-hidden truncate cursor-pointer"
                                        @mouseover="
                                            showCardTitleTooltip =
                                                shouldTruncate(
                                                    benefitItem.card.title
                                                )
                                        "
                                        @mouseleave="
                                            showCardTitleTooltip = false
                                        "
                                    >
                                        {{
                                            truncateString(
                                                benefitItem.card.title ||
                                                    'Unknown Card'
                                            )
                                        }}
                                    </span>
                                    <div
                                        v-show="showCardTitleTooltip"
                                        class="absolute bottom-full left-0 mb-2 py-1 px-2 text-sm text-white bg-slate-800 rounded-lg shadow-lg z-50 whitespace-nowrap"
                                    >
                                        {{
                                            benefitItem.card.title ||
                                            'Unknown Card'
                                        }}
                                    </div>
                                </div>
                                <span
                                    v-if="
                                        showLastFour &&
                                        benefitItem.connected_account_last_four
                                    "
                                    class="text-slate-500 dark:text-slate-400 text-xs shrink-0 whitespace-nowrap"
                                >
                                    ({{
                                        benefitItem.connected_account_last_four
                                    }})
                                </span>
                            </div>
                        </div>
                        <!-- Count-based benefit display -->
                        <div
                            class="text-slate-500 dark:text-slate-400 text-sm py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg max-w-fit font-medium"
                            v-else-if="isCountBased"
                        >
                            Up to
                            {{ benefitItem.benefit.value_details?.count }}
                            times/{{
                                store.getPeriodTypeFormat(
                                    benefitItem.benefit.period_type,
                                    'noun'
                                )
                            }}
                        </div>
                        <!-- Monetary benefit display -->
                        <div
                            class="text-slate-500 dark:text-slate-400 text-sm py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg max-w-fit font-medium"
                            v-else
                        >
                            Up to ${{
                                filters.formatThousand(displayAmount)
                            }}/{{
                                store.getPeriodTypeFormat(
                                    benefitItem.benefit.period_type,
                                    'noun'
                                )
                            }}
                        </div>
                    </div>
                </div>
                <!-- User Column -->
                <div
                    v-else-if="column.key === 'user'"
                    class="px-6 flex items-center"
                    :class="column.width || 'w-48'"
                >
                    <div
                        class="flex items-center gap-2"
                        v-if="viewMode === 'flat'"
                    >
                        <div
                            class="flex h-6 w-6 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                        >
                            <Icon
                                :icon="
                                    benefitItem.user?.is_business
                                        ? 'business'
                                        : 'user'
                                "
                                class="w-4 h-4 text-slate-500 dark:text-slate-400"
                            />
                        </div>
                        <div class="flex flex-col items-start min-w-0 flex-1">
                            <div
                                class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 line-clamp-2 relative group"
                            >
                                {{ benefitItem.user?.name || 'Unknown User' }}

                                <!-- Tooltip for full user name - always show on hover -->
                                <div
                                    class="absolute left-0 top-full mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg z-50 pointer-events-none whitespace-normal max-w-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                >
                                    {{
                                        benefitItem.user?.name || 'Unknown User'
                                    }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Empty space when viewMode is 'by_card' to maintain column alignment -->
                </div>

                <!-- Value Column -->
                <div
                    v-else-if="column.key === 'value_up_to'"
                    class="px-6 flex items-center justify-end"
                    :class="[
                        column.width,
                        { 'opacity-50': benefitItem.isLocked },
                    ]"
                >
                    <div
                        v-if="
                            !showIneligibleMessage &&
                            benefitItem.is_active !== false
                        "
                        class="py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center"
                    >
                        <p
                            v-if="isNcred"
                            class="text-slate-500 dark:text-slate-400 font-medium rounded text-sm"
                        >
                            {{ displayAmount }}
                            {{
                                filters.pluralizeUnit(
                                    displayAmount,
                                    benefitItem.benefit?.values_summary
                                        ?.unit_label || 'night credits'
                                )
                            }}/{{
                                store.getPeriodTypeFormat(
                                    benefitItem.benefit.period_type,
                                    'noun'
                                )
                            }}
                        </p>
                        <p
                            v-else-if="!isCountBased"
                            class="text-slate-500 dark:text-slate-400 font-medium rounded text-sm"
                        >
                            {{ filters.formatSmartMoney(displayAmount) }}/{{
                                store.getPeriodTypeFormat(
                                    benefitItem.benefit.period_type,
                                    'noun'
                                )
                            }}
                        </p>
                        <p
                            v-else
                            class="text-slate-500 dark:text-slate-400 font-medium rounded text-sm"
                        >
                            {{ benefitItem.benefit?.value_details?.count || 0 }}
                            per
                            {{
                                store.getPeriodTypeFormat(
                                    benefitItem.benefit.period_type,
                                    'noun'
                                )
                            }}
                        </p>
                        <!-- First Year Notice Icon -->
                        <Tooltip
                            v-if="benefitItem.benefit.show_first_year_notice"
                            x-placement="center"
                            y-placement="top"
                            class="shrink-0"
                            text="This benefit is only available after your card's first anniversary"
                        >
                            <template #trigger>
                                <Icon
                                    icon="circle-info"
                                    class="ml-1 w-4 h-4 cursor-help"
                                />
                            </template>
                        </Tooltip>
                    </div>
                </div>

                <!-- Usage Column -->
                <div
                    v-else-if="column.key === 'used_current_period'"
                    class="px-6 gap-2 flex flex-col"
                    :class="[column.width, { '-ml-12': isAGroup }]"
                >
                    <!-- Locked state: show "Upgrade to track" link (same structure as usage display) -->
                    <div v-if="benefitItem.isLocked">
                        <button
                            class="flex items-center gap-1 text-orange text-sm font-medium hover:underline cursor-pointer bg-transparent border-none p-0"
                            @click.stop="openUpgradeModal"
                        >
                            <Icon icon="lock" class="w-4 h-4" />
                            <span>Upgrade to track</span>
                        </button>
                    </div>
                    <div
                        v-else-if="showIneligibleMessage"
                        class="text-sm text-slate-500 dark:text-slate-400 font-normal"
                    >
                        Ineligible until
                        {{ store.formatYear(benefitItem.period_end_date) }}
                    </div>
                    <div v-else>
                        <div
                            class="w-full max-w-[120px] bg-orange-200 rounded-full h-1 relative"
                        >
                            <div
                                class="bg-10x-orange h-1 rounded-full transition-all"
                                :style="{
                                    width: calculatedUsagePercentage + '%',
                                }"
                            ></div>
                            <!-- 100% Completion Indicator -->
                            <div
                                v-if="showFullyRedeemedIcon"
                                class="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-10x-orange rounded-full flex items-center justify-center border-2 border-white"
                            >
                                <Icon
                                    icon="check"
                                    svgClass="w-2 h-2 text-white"
                                    class="text-white"
                                />
                            </div>
                        </div>
                        <div
                            class="flex items-center text-sm text-slate-500 dark:text-slate-400 font-normal mt-2"
                        >
                            <div class="flex items-center">
                                <!-- Count-based: "2 of 4 used" -->
                                <!-- NCRED: "0 of 1 night credits" -->
                                <!-- Monetary: "$50 of $250" -->
                                <span>
                                    <template v-if="isCountBased">
                                        {{ effectiveCurrentPeriodUsage }}
                                    </template>
                                    <template v-else>
                                        {{
                                            filters.formatSmartMoney(
                                                effectiveCurrentPeriodUsage
                                            )
                                        }}
                                    </template>
                                </span>
                                <span class="ml-1">
                                    of
                                    <template v-if="isNcred">
                                        {{ displayAmount }}
                                        {{
                                            filters.pluralizeUnit(
                                                displayAmount,
                                                benefitItem.benefit
                                                    ?.values_summary
                                                    ?.unit_label ||
                                                    'night credits'
                                            )
                                        }}
                                    </template>
                                    <template v-else-if="isCountBased">
                                        {{
                                            benefitItem.benefit?.value_details
                                                ?.count || 0
                                        }}
                                        used
                                    </template>
                                    <template v-else>
                                        {{
                                            filters.formatSmartMoney(
                                                displayAmount || 0
                                            )
                                        }}
                                    </template>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Expires In Column -->
                <div
                    v-else-if="column.key === 'expires_in'"
                    class="px-6 gap-1 flex flex-col"
                    :class="[
                        column.width,
                        { 'opacity-50': benefitItem.isLocked },
                    ]"
                >
                    <div
                        v-if="
                            expirationTime &&
                            expirationFrequency &&
                            benefitItem.is_active !== false
                        "
                    >
                        <div class="text-slate-800 dark:text-slate-100 font-normal text-sm">
                            {{ expirationTime }}
                        </div>
                        <div
                            v-show="expirationFrequency"
                            class="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-1 whitespace-nowrap"
                        >
                            <Icon icon="arrows-repeat" class="h-4 w-4" />
                            {{ expirationFrequency }}
                        </div>
                    </div>
                </div>
                <!-- Category Column -->
                <div
                    v-else-if="column.key === 'category'"
                    class="px-6"
                    :class="column.width"
                >
                    <span
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                        {{ benefitItem.benefit_category || 'Uncategorized' }}
                    </span>
                </div>
            </template>

            <!-- Actions Column (Always Last) -->
            <div class="shrink-0 justify-end flex">
                <div class="flex items-center space-x-2 px-6">
                    <!-- Placeholder for locked benefits to maintain column width -->
                    <div v-if="benefitItem.isLocked" class="w-9 h-9"></div>
                    <!-- Show add usage button for unlocked benefits only -->
                    <template v-else>
                        <button
                            v-if="isBenefitFullyRedeemedThisYear"
                            class="btn-icon btn-white opacity-50 cursor-not-allowed !bg-white dark:bg-slate-800"
                            disabled
                        >
                            <Icon
                                icon="plus"
                                svgClass="w-4 h-4 text-slate-600 dark:text-slate-300"
                            />
                        </button>
                        <ButtonPopover
                            v-else
                            buttonClass="btn-icon btn-white"
                            popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg min-w-80 z-40 outline outline-1 outline-offset-[-1px] outline-slate-200"
                            :teleport="true"
                            @click.stop
                            @open="resetSubBenefitPicker"
                        >
                            <template #button-content>
                                <span
                                    class="inline-flex"
                                    @click="addUsageMode = 'menu'"
                                >
                                    <Icon
                                        icon="plus"
                                        svgClass="w-4 h-4 text-slate-600 dark:text-slate-300"
                                    />
                                </span>
                            </template>
                            <template #default="{ closePopover }">
                                <!-- MY1-3080: an auto-tracked benefit first chooses between
                                     a My Spending transaction and manual entry -->
                                <AddUsageMenu
                                    v-if="showSpendingOption && addUsageMode === 'menu'"
                                    @spending="
                                        closePopover();
                                        openTransactionPicker();
                                    "
                                    @manual="addUsageMode = 'manual'"
                                />
                                <template v-else>
                                <!-- Compound benefit: sub-benefit picker or usage form -->
                                <div v-if="hasSubBenefits" class="w-[360px]">
                                    <!-- Step 1: Pick which sub-benefit to log against -->
                                    <div v-if="!activeSubBenefit">
                                        <div
                                            class="px-4 pt-4 pb-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                                        >
                                            Log usage for:
                                        </div>
                                        <div
                                            v-for="sub in liveBenefit.sub_benefits"
                                            :key="sub.id"
                                            class="flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 cursor-pointer border-t border-slate-100"
                                            @click="selectSubBenefit(sub)"
                                        >
                                            <div>
                                                <div
                                                    class="text-sm font-medium text-slate-800 dark:text-slate-100"
                                                >
                                                    {{ sub.benefit_title }}
                                                </div>
                                                <div
                                                    class="text-xs text-slate-500 dark:text-slate-400 mt-0.5"
                                                >
                                                    <template
                                                        v-if="
                                                            sub.values_summary
                                                                ?.is_count_based
                                                        "
                                                    >
                                                        {{
                                                            subCreditsUsed(sub)
                                                        }}
                                                        of
                                                        {{
                                                            sub.value_details
                                                                ?.count || 0
                                                        }}
                                                        {{
                                                            sub.value_details
                                                                ?.count_label ||
                                                            'credits'
                                                        }}
                                                        used
                                                    </template>
                                                    <template v-else>
                                                        {{
                                                            filters.formatSmartMoney(
                                                                sub.current_period_usage
                                                            )
                                                        }}
                                                        of
                                                        {{
                                                            filters.formatSmartMoney(
                                                                sub.amount
                                                            )
                                                        }}
                                                    </template>
                                                </div>
                                            </div>
                                            <Icon
                                                icon="chevron-right"
                                                svgClass="w-4 h-4 text-slate-400"
                                            />
                                        </div>
                                    </div>

                                    <!-- Step 2: Usage form for selected sub-benefit -->
                                    <div v-else class="p-4">
                                        <button
                                            class="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:text-slate-100 mb-3"
                                            @click="activeSubBenefit = null"
                                        >
                                            <Icon
                                                icon="chevron-left"
                                                svgClass="w-3 h-3"
                                            />
                                            Back
                                        </button>
                                        <div
                                            class="text-sm font-medium text-slate-800 dark:text-slate-100 mb-3"
                                        >
                                            {{ activeSubBenefit.benefit_title }}
                                        </div>
                                        <BenefitsUsageForm
                                            v-model="subUsageFormData"
                                            :hideLabels="true"
                                            :errors="subFormErrors"
                                            :isCountBased="
                                                activeSubBenefit.values_summary
                                                    ?.is_count_based
                                            "
                                            :countLabel="
                                                activeSubBenefit.value_details
                                                    ?.count_label || ''
                                            "
                                            :maxAmount="
                                                subMaxFillable(activeSubBenefit)
                                                    .amount
                                            "
                                            :maxCount="
                                                subMaxFillable(activeSubBenefit)
                                                    .count
                                            "
                                        />
                                        <div
                                            class="flex justify-between items-center mt-4"
                                        >
                                            <div
                                                class="text-slate-500 dark:text-slate-400 text-sm"
                                                v-if="subRemainingDisplay"
                                            >
                                                {{ subRemainingDisplay }}
                                                remaining
                                            </div>
                                            <Errors
                                                v-else
                                                :errors="[
                                                    {
                                                        message:
                                                            activeSubBenefit
                                                                .values_summary
                                                                ?.is_count_based
                                                                ? 'No remaining uses!'
                                                                : 'No remaining value!',
                                                    },
                                                ]"
                                            />
                                            <button
                                                :disabled="
                                                    subRedeemDisabledForActive
                                                "
                                                @click="
                                                    handleSubBenefitSubmit(
                                                        closePopover
                                                    )
                                                "
                                                class="bg-slate-800 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-slate-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-800"
                                            >
                                                <svg
                                                    class="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                                <span>Redeem</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Regular benefit: standard usage form -->
                                <div v-else class="p-4 w-[360px]">
                                    <BenefitsUsageForm
                                        v-model="usageFormData"
                                        :hideLabels="true"
                                        :errors="formErrors"
                                        :isCountBased="isCountBased"
                                        :countLabel="countLabel"
                                        :maxAmount="maxFillableAmount"
                                        :maxCount="remainingCount"
                                    />
                                    <div
                                        class="flex justify-between items-center mt-4"
                                        v-if="!isCountBased"
                                    >
                                        <div
                                            class="text-slate-500 dark:text-slate-400 text-sm"
                                            v-if="
                                                isByCountValueBenefit
                                                    ? parseFloat(
                                                          remainingCount
                                                      ) > 0
                                                    : parseFloat(
                                                          remainingAmount
                                                      ) > 0
                                            "
                                        >
                                            <template
                                                v-if="isByCountValueBenefit"
                                            >
                                                {{ remainingCount }} remaining
                                            </template>
                                            <template v-else>
                                                ${{ remainingAmount }} remaining
                                            </template>
                                        </div>
                                        <Errors
                                            v-else
                                            :errors="[
                                                {
                                                    message:
                                                        isByCountValueBenefit
                                                            ? 'No remaining uses!'
                                                            : 'No remaining value!',
                                                },
                                            ]"
                                        />
                                        <button
                                            @click="
                                                handleUsageSubmit(closePopover)
                                            "
                                            class="bg-slate-800 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-slate-700 transition-colors flex items-center space-x-2"
                                        >
                                            <svg
                                                class="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                            <span>Redeem</span>
                                        </button>
                                    </div>
                                    <div
                                        class="flex justify-between items-center mt-4"
                                        v-else
                                    >
                                        <div
                                            class="text-slate-500 dark:text-slate-400 text-sm"
                                            v-if="
                                                parseFloat(remainingCount) > 0
                                            "
                                        >
                                            {{ remainingCount }} remaining
                                        </div>
                                        <Errors
                                            v-else
                                            :errors="[
                                                {
                                                    message:
                                                        'No remaining uses!',
                                                },
                                            ]"
                                        />
                                        <button
                                            @click="
                                                handleUsageSubmit(closePopover)
                                            "
                                            class="bg-slate-800 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-slate-700 transition-colors flex items-center space-x-2"
                                        >
                                            <svg
                                                class="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                            <span>Redeem</span>
                                        </button>
                                    </div>
                                </div>
                                </template>
                            </template>
                        </ButtonPopover>
                    </template>

                    <!-- Chevron button - visible but blocked for locked benefits -->
                    <button
                        class="btn-icon !rounded-full"
                        :class="{
                            'opacity-50 cursor-default': benefitItem.isLocked,
                        }"
                        @click="
                            !benefitItem.isLocked &&
                            $emit('view-details', benefitItem)
                        "
                    >
                        <Icon icon="chevron-right" svgClass="w-3 h-3 m-1" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Expanded sub-benefit rows -->
        <template v-if="hasSubBenefits && isExpanded">
            <div
                v-for="(sub, subIndex) in liveBenefit.sub_benefits"
                :key="sub.id"
                class="flex items-center bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700 py-4 relative"
            >
                <!-- Tree connector -->
                <div
                    class="absolute bg-slate-50 dark:bg-slate-900/40 w-16 -left-0 -top-0 bottom-0 h-full flex items-center justify-end pr-1"
                >
                    <svg
                        :width="12"
                        :height="subIndex === 0 ? 70 : 110"
                        :viewBox="subIndex === 0 ? '0 0 12 70' : '0 0 12 110'"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="absolute left-6"
                        :class="subIndex === 0 ? '-top-2' : '-top-12'"
                    >
                        <path
                            v-if="subIndex === 0"
                            d="M1.5 5V50Q1.5 63 12 63"
                            stroke="#CBD5E1"
                            stroke-width="1"
                            stroke-linecap="round"
                        />
                        <path
                            v-else
                            d="M1.5 0V85Q1.5 98 12 98"
                            stroke="#CBD5E1"
                            stroke-width="1"
                            stroke-linecap="round"
                        />
                    </svg>
                </div>

                <!-- Sub-benefit columns matching parent structure -->
                <template v-for="column in filteredColumns" :key="column.key">
                    <!-- Benefit column -->
                    <div
                        v-if="column.key === 'benefit'"
                        class="flex items-center min-w-0 pl-12"
                        :class="column.width"
                    >
                        <div class="relative shrink-0 mr-4">
                            <BenefitIcon
                                :category="benefitItem.benefit.category"
                                :title="benefitItem.benefit.benefit_title"
                                :description="
                                    benefitItem.benefit.benefit_description
                                "
                                svg-class="w-5 h-5 text-slate-500 dark:text-slate-400"
                            />
                        </div>
                        <div class="min-w-0 flex-1">
                            <div
                                class="font-medium text-sm text-slate-700 dark:text-slate-200 line-clamp-1"
                            >
                                {{ sub.benefit_title }}
                            </div>
                            <div
                                class="text-slate-500 dark:text-slate-400 text-sm py-0.5 px-2 bg-white dark:bg-slate-800 rounded-lg max-w-fit font-medium border border-slate-100 mt-1"
                            >
                                <template
                                    v-if="sub.values_summary?.is_count_based"
                                >
                                    {{ filters.formatSmartMoney(sub.amount) }}
                                    &ndash; Qty:
                                    {{ sub.value_details?.count || 0 }} per
                                    {{
                                        store.getPeriodTypeFormat(
                                            sub.period_type,
                                            'noun'
                                        )
                                    }}
                                </template>
                                <template v-else>
                                    Up to
                                    {{
                                        filters.formatSmartMoney(sub.amount)
                                    }}/{{
                                        store.getPeriodTypeFormat(
                                            sub.period_type,
                                            'noun'
                                        )
                                    }}
                                </template>
                            </div>
                        </div>
                    </div>
                    <!-- User column for sub-benefits (same as parent) -->
                    <div
                        v-else-if="column.key === 'user'"
                        :class="column.width || 'w-48'"
                        class="px-6 flex items-center"
                    >
                        <div
                            class="flex items-center gap-2"
                            v-if="viewMode === 'flat'"
                        >
                            <div
                                class="flex h-6 w-6 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                            >
                                <Icon
                                    :icon="
                                        benefitItem.user?.is_business
                                            ? 'business'
                                            : 'user'
                                    "
                                    class="w-4 h-4 text-slate-500 dark:text-slate-400"
                                />
                            </div>
                            <div
                                class="flex flex-col items-start min-w-0 flex-1"
                            >
                                <div
                                    class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 line-clamp-2 relative group"
                                >
                                    {{
                                        benefitItem.user?.name || 'Unknown User'
                                    }}
                                    <div
                                        class="absolute left-0 top-full mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg z-50 pointer-events-none whitespace-normal max-w-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                    >
                                        {{
                                            benefitItem.user?.name ||
                                            'Unknown User'
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Value up to column for sub-benefits -->
                    <div
                        v-else-if="column.key === 'value_up_to'"
                        :class="column.width"
                        class="px-6 flex items-center justify-end"
                    >
                        <div
                            class="py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center"
                        >
                            <p
                                class="text-slate-500 dark:text-slate-400 font-medium rounded text-sm"
                            >
                                <template
                                    v-if="sub.values_summary?.is_count_based"
                                >
                                    {{ sub.value_details?.count || 0 }} per
                                    {{
                                        store.getPeriodTypeFormat(
                                            sub.period_type,
                                            'noun'
                                        )
                                    }}
                                </template>
                                <template v-else>
                                    {{
                                        filters.formatSmartMoney(sub.amount)
                                    }}/{{
                                        store.getPeriodTypeFormat(
                                            sub.period_type,
                                            'noun'
                                        )
                                    }}
                                </template>
                            </p>
                        </div>
                    </div>
                    <!-- Usage column -->
                    <div
                        v-else-if="column.key === 'used_current_period'"
                        class="px-6 gap-2 flex flex-col"
                        :class="column.width"
                    >
                        <div>
                            <div
                                class="w-full max-w-[120px] bg-orange-200 rounded-full h-1 relative"
                            >
                                <div
                                    class="bg-10x-orange h-1 rounded-full transition-all"
                                    :style="{
                                        width: subUsagePercentage(sub) + '%',
                                    }"
                                ></div>
                            </div>
                            <div
                                class="flex items-center text-sm text-slate-500 dark:text-slate-400 font-normal mt-2"
                            >
                                <template
                                    v-if="sub.values_summary?.is_count_based"
                                >
                                    {{ subCreditsUsed(sub) }} of
                                    {{ sub.value_details?.count || 0 }}
                                    {{
                                        sub.value_details?.count_label ||
                                        'credits'
                                    }}
                                    used
                                </template>
                                <template v-else>
                                    {{
                                        filters.formatSmartMoney(
                                            sub.current_period_usage
                                        )
                                    }}
                                    of
                                    {{ filters.formatSmartMoney(sub.amount) }}
                                </template>
                            </div>
                        </div>
                    </div>
                    <!-- Expires In column for sub-benefits -->
                    <div
                        v-else-if="column.key === 'expires_in'"
                        :class="column.width"
                        class="px-6 gap-1 flex flex-col"
                    >
                        <div v-if="sub.period_end_date && sub.period_type">
                            <div class="text-slate-800 dark:text-slate-100 font-normal text-sm">
                                {{
                                    store.getExpirationTime(
                                        sub.period_end_date,
                                        sub.period_type
                                    )
                                }}
                            </div>
                            <div
                                class="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-1 whitespace-nowrap"
                            >
                                <Icon icon="arrows-repeat" class="h-4 w-4" />
                                <template
                                    v-if="
                                        sub.reset_basis === 'card_anniversary'
                                    "
                                >
                                    Cardmember year
                                </template>
                                <template v-else>
                                    {{
                                        store
                                            .formatPeriodType(sub.period_type)
                                            .charAt(0)
                                            .toUpperCase() +
                                        store
                                            .formatPeriodType(sub.period_type)
                                            .slice(1)
                                            .toLowerCase()
                                    }}
                                </template>
                            </div>
                        </div>
                    </div>
                    <!-- Category column: empty for sub-benefits -->
                    <div
                        v-else-if="column.key === 'category'"
                        :class="column.width"
                        class="px-6"
                    ></div>
                </template>

                <!-- Sub-benefit actions -->
                <div class="shrink-0 justify-end flex">
                    <div class="flex items-center space-x-2 px-6">
                        <ButtonPopover
                            buttonClass="btn-icon btn-white"
                            popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg min-w-80 z-40 outline outline-1 outline-offset-[-1px] outline-slate-200"
                            :teleport="true"
                            @click.stop
                        >
                            <template #button-content>
                                <Icon
                                    icon="plus"
                                    svgClass="w-4 h-4 text-slate-600 dark:text-slate-300"
                                />
                            </template>
                            <template #default="{ closePopover }">
                                <div class="p-4 w-[360px]">
                                    <div
                                        class="text-sm font-medium text-slate-800 dark:text-slate-100 mb-3"
                                    >
                                        {{ sub.benefit_title }}
                                    </div>
                                    <BenefitsUsageForm
                                        v-model="inlineSubFormData[sub.id]"
                                        :hideLabels="true"
                                        :errors="
                                            inlineSubFormErrors[sub.id] || {}
                                        "
                                        :isCountBased="
                                            sub.values_summary?.is_count_based
                                        "
                                        :countLabel="
                                            sub.value_details?.count_label || ''
                                        "
                                        :maxAmount="subMaxFillable(sub).amount"
                                        :maxCount="subMaxFillable(sub).count"
                                    />
                                    <div
                                        class="flex justify-between items-center mt-4"
                                    >
                                        <div
                                            class="text-slate-500 dark:text-slate-400 text-sm"
                                            v-if="
                                                subInlineRemainingDisplay(
                                                    sub
                                                ) !== null
                                            "
                                        >
                                            {{ subInlineRemainingDisplay(sub) }}
                                            remaining
                                        </div>
                                        <Errors
                                            v-else
                                            :errors="[
                                                {
                                                    message: sub.values_summary
                                                        ?.is_count_based
                                                        ? 'No remaining uses!'
                                                        : 'No remaining value!',
                                                },
                                            ]"
                                        />
                                        <button
                                            :disabled="
                                                subInlineRedeemDisabled(sub)
                                            "
                                            @click="
                                                handleInlineSubBenefitSubmit(
                                                    sub,
                                                    closePopover
                                                )
                                            "
                                            class="bg-slate-800 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-slate-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-800"
                                        >
                                            <svg
                                                class="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                            <span>Redeem</span>
                                        </button>
                                    </div>
                                </div>
                            </template>
                        </ButtonPopover>
                        <!-- Spacer to align with parent chevron -->
                        <div class="w-9 h-9"></div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import BenefitIcon from '@/components/card-benefits/benefit-icon.vue';
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import BenefitsUsageForm from '@/components/card-benefits/benefits-usage-form.vue';
import Tooltip from '@/components/tooltip.vue';
import BenefitAutoTrackingLabel from '@/components/card-benefits/benefit-auto-tracking-label.vue';
import AddUsageMenu from '@/components/card-benefits/add-usage-menu.vue';
import dayjs from 'dayjs';
import filters from '@/filters';

const props = defineProps({
    benefitItem: {
        type: Object,
        required: true,
    },
    columns: {
        type: Array,
        required: true,
    },
    isAGroup: {
        type: Boolean,
        default: false,
    },
    index: {
        type: Number,
        default: 0,
    },
});

// Sub-benefit state
const isExpanded = ref((props.benefitItem?.sub_benefits?.length ?? 0) > 0);
const activeSubBenefit = ref(null);
const subUsageFormData = ref({
    amount: 0,
    date: dayjs().format('YYYY-MM-DD'),
    count: 0,
});
const subFormErrors = ref({});
const inlineSubFormData = ref({});
const inlineSubFormErrors = ref({});

const emit = defineEmits(['add-usage', 'view-details']);

const store = useCardBenefitsStore();
const { showLastFour, viewMode } = storeToRefs(store);
const { openBenefitDetails, calculateDisplayAmount } = store;

const formErrors = ref({});

// Get live benefit from store to ensure reactivity to optimistic updates
const liveBenefit = computed(() => {
    return (
        store.benefits.find(
            (b) =>
                b.benefit.id === props.benefitItem.benefit.id &&
                b.connected_account_id ===
                    props.benefitItem.connected_account_id
        ) || props.benefitItem
    );
});

const filteredColumns = computed(() => {
    return props.columns.filter((column) => column.show);
});

const isNcred = computed(() => {
    return props.benefitItem?.benefit?.values_summary?.currency === 'NCRED';
});

const isCountBased = computed(() => {
    const benefit = props.benefitItem?.benefit;
    return store.checkIsCountBased(benefit);
});

// By-count benefits that still carry a dollar value (e.g. 2 × $250): the user
// enters a $ amount (so isCountBased is false and the form shows the amount
// input), but the meaningful "remaining" unit is the number of uses — the
// backend caps these at value_details.count via ValidateBenefitUsageCount.
// Used to swap the remaining indicator to a use count and to block once the
// per-period uses are exhausted.
const isByCountValueBenefit = computed(() => {
    if (isCountBased.value) {
        return false;
    }
    const count =
        parseFloat(liveBenefit.value?.benefit?.value_details?.count) || 1;
    return count > 1;
});

const countLabel = computed(() => {
    return props.benefitItem?.benefit?.value_details?.count_label || '';
});

const hasSubBenefits = computed(() => {
    return (liveBenefit.value?.sub_benefits?.length ?? 0) > 0;
});

// Eagerly initialise inline form data for every sub-benefit. ButtonPopover
// doesn't emit 'open', so we can't lazily init on popover open.
watch(
    () => liveBenefit.value?.sub_benefits,
    (subs) => {
        if (!subs) return;
        subs.forEach((sub) => {
            if (!inlineSubFormData.value[sub.id]) {
                inlineSubFormData.value[sub.id] = {
                    amount: '',
                    date: dayjs().format('YYYY-MM-DD'),
                    count: 0,
                };
            }
        });
    },
    { immediate: true }
);

// Aggregate current_period_usage across sub-benefits (for compound benefit display)
const effectiveCurrentPeriodUsage = computed(() => {
    if (!hasSubBenefits.value) {
        return currentPeriodUsage.value;
    }
    return (liveBenefit.value.sub_benefits || []).reduce(
        (sum, s) => sum + (parseFloat(s.current_period_usage) || 0),
        0
    );
});

const subCreditsUsed = (sub) => {
    const perUnit = parseFloat(sub.amount || 0);
    if (sub.values_summary?.is_count_based && perUnit > 0) {
        return Math.floor(parseFloat(sub.current_period_usage || 0) / perUnit);
    }
    return parseFloat(sub.current_period_usage || 0);
};

const subUsagePercentage = (sub) => {
    const max = sub.values_summary?.is_count_based
        ? parseFloat(sub.value_details?.count || 0)
        : parseFloat(sub.amount || 0);
    const used = sub.values_summary?.is_count_based
        ? subCreditsUsed(sub)
        : parseFloat(sub.current_period_usage || 0);
    return max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
};

const selectSubBenefit = (sub) => {
    activeSubBenefit.value = sub;
    subUsageFormData.value = {
        amount: 0,
        date: dayjs().format('YYYY-MM-DD'),
        count: 0,
    };
    subFormErrors.value = {};
};

// MY1-3080: "Add from My Spending" — the source menu shows for auto-tracked
// benefits; 'menu' → choose, 'manual' → the existing form below.
const addUsageMode = ref('menu');
const showSpendingOption = computed(
    () => props.benefitItem?.auto_tracking?.state === 'auto_tracked'
);
const openTransactionPicker = () => {
    store.openTransactionPicker(props.benefitItem, remainingAmount.value);
};

const resetSubBenefitPicker = () => {
    activeSubBenefit.value = null;
    subFormErrors.value = {};
};

const resetSubForm = (sub) => {
    inlineSubFormData.value[sub.id] = {
        amount: '',
        date: dayjs().format('YYYY-MM-DD'),
        count: 0,
    };
    inlineSubFormErrors.value[sub.id] = {};
};

// Returns null only when the sub-benefit is *actually* exhausted. When the
// user types a value that would push remaining to 0 (a valid redemption of
// the last credit) we still return the preview string ("0", "$0.00") so the
// Redeem button stays enabled — disabled state is computed in
// subRedeemDisabledForActive / subInlineRedeemDisabled below.
const subRemainingDisplay = computed(() => {
    if (!activeSubBenefit.value) {
        return null;
    }
    const sub = activeSubBenefit.value;
    const isCountBased = sub.values_summary?.is_count_based;
    if (isCountBased) {
        const total = parseFloat(sub.value_details?.count || 0);
        const used = subCreditsUsed(sub);
        if (total - used <= 0) return null;
        const remaining = Math.max(
            0,
            total - used - parseFloat(subUsageFormData.value.count || 0)
        );
        return `${remaining}`;
    }
    const total = parseFloat(sub.amount || 0);
    const used = parseFloat(sub.current_period_usage || 0);
    if (total - used <= 0) return null;
    const remaining = Math.max(
        0,
        total - used - parseFloat(subUsageFormData.value.amount || 0)
    );
    return filters.formatSmartMoney(remaining);
});

// Max value the "Max" pill fills for a sub-benefit usage form — the sub's full
// remaining for the period. Count-based subs fill the count input, monetary
// subs fill the amount input. Mirrors the remaining math in
// subRemainingDisplay / subInlineRemainingDisplay.
const subMaxFillable = (sub) => {
    if (!sub) {
        return { amount: null, count: null };
    }
    if (sub.values_summary?.is_count_based) {
        const total = parseFloat(sub.value_details?.count || 0);
        const used = subCreditsUsed(sub);
        return { amount: null, count: Math.max(0, total - used) };
    }
    const total = parseFloat(sub.amount || 0);
    const used = parseFloat(sub.current_period_usage || 0);
    return { amount: Math.max(0, total - used), count: null };
};

const subInlineRemainingDisplay = (sub) => {
    const formData = inlineSubFormData.value[sub.id] || {};
    const isCountBased = sub.values_summary?.is_count_based;
    if (isCountBased) {
        const total = parseFloat(sub.value_details?.count || 0);
        const used = subCreditsUsed(sub);
        if (total - used <= 0) return null; // already fully redeemed
        const remaining = Math.max(
            0,
            total - used - parseFloat(formData.count || 0)
        );
        return `${remaining}`;
    }
    const total = parseFloat(sub.amount || 0);
    const used = parseFloat(sub.current_period_usage || 0);
    if (total - used <= 0) return null; // already fully redeemed
    const remaining = Math.max(
        0,
        total - used - parseFloat(formData.amount || 0)
    );
    return filters.formatSmartMoney(remaining);
};

// Disable Redeem only when the submission would be invalid: the sub-benefit
// is exhausted, or the typed value exceeds the actual remaining. Typing the
// exact remaining amount stays valid (the user is redeeming their last credit).
const subRedeemDisabledForActive = computed(() => {
    const sub = activeSubBenefit.value;
    if (!sub) return true;
    const isCountBased = sub.values_summary?.is_count_based;
    if (isCountBased) {
        const total = parseFloat(sub.value_details?.count || 0);
        const used = subCreditsUsed(sub);
        const typed = parseFloat(subUsageFormData.value.count || 0);
        return total - used <= 0 || typed > total - used;
    }
    const total = parseFloat(sub.amount || 0);
    const used = parseFloat(sub.current_period_usage || 0);
    const typed = parseFloat(subUsageFormData.value.amount || 0);
    return total - used <= 0 || typed > total - used;
});

const subInlineRedeemDisabled = (sub) => {
    const formData = inlineSubFormData.value[sub.id] || {};
    const isCountBased = sub.values_summary?.is_count_based;
    if (isCountBased) {
        const total = parseFloat(sub.value_details?.count || 0);
        const used = subCreditsUsed(sub);
        const typed = parseFloat(formData.count || 0);
        return total - used <= 0 || typed > total - used;
    }
    const total = parseFloat(sub.amount || 0);
    const used = parseFloat(sub.current_period_usage || 0);
    const typed = parseFloat(formData.amount || 0);
    return total - used <= 0 || typed > total - used;
};

const handleSubBenefitSubmit = async (closePopover) => {
    const sub = activeSubBenefit.value;
    if (!sub) {
        return;
    }
    subFormErrors.value = {};
    const isCountBased = sub.values_summary?.is_count_based;
    if (isCountBased) {
        if (!subUsageFormData.value.count) {
            subFormErrors.value.usageCount = 'Enter count';
            return;
        }
    } else {
        if (!subUsageFormData.value.amount) {
            subFormErrors.value.usageAmount = 'Enter amount';
            return;
        }
        if (!subUsageFormData.value.date) {
            subFormErrors.value.usageDate = 'Enter date';
            return;
        }
    }

    const success = await store.handleSubBenefitUsageSubmit(
        subUsageFormData.value,
        sub,
        props.benefitItem.connected_account_id
    );

    if (success) {
        subUsageFormData.value = {
            amount: '',
            count: null,
            date: dayjs().format('YYYY-MM-DD'),
        };
        activeSubBenefit.value = null;
        closePopover();
    }
};

const handleInlineSubBenefitSubmit = async (sub, closePopover) => {
    const formData = inlineSubFormData.value[sub.id] || {};
    inlineSubFormErrors.value[sub.id] = {};
    const isCountBased = sub.values_summary?.is_count_based;
    if (isCountBased) {
        if (!formData.count) {
            inlineSubFormErrors.value[sub.id] = { usageCount: 'Enter count' };
            return;
        }
    } else {
        if (!(parseFloat(formData.amount) > 0)) {
            inlineSubFormErrors.value[sub.id] = { usageAmount: 'Enter amount' };
            return;
        }
        if (!formData.date) {
            inlineSubFormErrors.value[sub.id] = { usageDate: 'Enter date' };
            return;
        }
    }

    const success = await store.handleSubBenefitUsageSubmit(
        formData,
        sub,
        props.benefitItem.connected_account_id
    );

    if (success) {
        inlineSubFormData.value[sub.id] = {
            amount: '',
            count: null,
            date: dayjs().format('YYYY-MM-DD'),
        };
        closePopover();
    }
};

const remainingCount = computed(() => {
    const usedCount = countUsedForSelectedPeriod.value || 0;
    const totalCount =
        liveBenefit.value?.benefit?.value_details?.count ||
        (isNcred.value ? displayAmount.value : 0) ||
        0;
    return Math.max(0, totalCount - usedCount);
});

const expirationTime = computed(() => {
    // Don't show expiration for TSA benefits with no usage records
    if (shouldHideExpirationForTsa.value) {
        return null;
    }

    return store.getExpirationTime(
        props.benefitItem?.period_end_date,
        props.benefitItem?.benefit?.period_type
    );
});

const shouldHideExpirationForTsa = computed(() => {
    const category = props.benefitItem?.benefit?.category?.toLowerCase();
    const periodType = props.benefitItem?.benefit?.period_type?.toLowerCase();
    const usageRecords = props.benefitItem?.usage_records || [];

    // Hide expiration if category is "tsa" with quinquennial or quadrennial period and no usage records
    // Don't hide for annual TSA benefits like CLEAR
    return (
        category === 'tsa' &&
        (periodType === 'quinquennial' || periodType === 'quadrennial') &&
        usageRecords.length === 0
    );
});

const showIneligibleMessage = computed(() => {
    return (
        !parseFloat(displayAmount.value) &&
        props.benefitItem.benefit.reset_basis === 'usage'
    );
});

const expirationFrequency = computed(() => {
    const periodType = store.formatPeriodType(
        props.benefitItem.benefit.period_type
    );
    const resetBasis = props.benefitItem.benefit.reset_basis;

    if (resetBasis === 'card_anniversary') {
        return 'Cardmember year';
    }

    return (
        periodType.charAt(0).toUpperCase() + periodType.slice(1).toLowerCase()
    );
});

const isBenefitTitleTruncated = computed(() => {
    const title = props.benefitItem.benefit.benefit_title || 'Unknown Benefit';
    // Consider title truncated if it's longer than 50 characters (approximate for 2 lines)
    return title.length > 50;
});

// Tooltip state
const showCardTitleTooltip = ref(false);
const showTooltip = ref(false);

// Truncation detection (matching wallet component pattern)
const shouldTruncate = (s) => {
    const hasCardNumber =
        showLastFour.value && props.benefitItem.connected_account_last_four;
    const maxLength = hasCardNumber ? 35 : 50;
    return s && s.length > maxLength;
};

const truncateString = (s) => {
    if (!s) return '';
    const hasCardNumber =
        showLastFour.value && props.benefitItem.connected_account_last_four;
    const maxLength = hasCardNumber ? 35 : 50;
    if (s.length <= maxLength) return s;
    return s.substring(0, maxLength) + '...';
};

const displayAmount = computed(() => {
    const benefit = liveBenefit.value;

    if (!benefit) return 0;

    if (benefit.benefit.period_type?.toLowerCase() === 'monthly') {
        return store.calculateMonthlyValue(benefit);
    }
    return calculateDisplayAmount(benefit);
});

// Find the version effective at the selected usage date so we can show the
// correct remaining value for retroactive entries against now-closed windows
// (e.g., a limited-time promotion that has since ended). SUPPORT-552.
const effectiveVersionForSelectedDate = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit || !usageFormData.value.date) return null;

    const versionHistory = benefit.version_history || [];
    if (versionHistory.length === 0) return null;

    const selectedDate = dayjs(usageFormData.value.date);

    const exactMatch = versionHistory.find((version) => {
        const effectiveStart = dayjs(version.effective_start);
        const effectiveEnd = version.effective_end
            ? dayjs(version.effective_end)
            : null;
        const afterStart =
            selectedDate.isSame(effectiveStart, 'day') ||
            selectedDate.isAfter(effectiveStart);
        const beforeEnd = !effectiveEnd || selectedDate.isBefore(effectiveEnd);
        return afterStart && beforeEnd;
    });

    if (exactMatch) return exactMatch;

    // Mirror the backend versionAtOrEarliest fallback: dates before the
    // earliest version assume the benefit existed before versioning existed.
    const sortedVersions = [...versionHistory].sort(
        (a, b) =>
            dayjs(a.effective_start).valueOf() -
            dayjs(b.effective_start).valueOf()
    );
    const earliestVersion = sortedVersions[0];

    if (
        earliestVersion &&
        selectedDate.isBefore(dayjs(earliestVersion.effective_start))
    ) {
        return earliestVersion;
    }

    return null;
});

const hasActiveVersionForSelectedDate = computed(() => {
    return effectiveVersionForSelectedDate.value !== null;
});

// Display amount for the selected date in the form (handles monthly overrides
// and version history)
// Count-based benefits multiply the per-use amount by the count (e.g. 2 × $50 =
// $100), or use the count itself as the limit when there's no dollar amount
// (e.g. 4 free nights → 4). Mirrors store.calculateDisplayAmount and the backend
// ValidateBenefitUsageAmount period limit so the form's "Max amount" matches.
const applyCountMultiplier = (amount, count, isCountBasedBenefit) => {
    if (!isCountBasedBenefit) {
        return amount;
    }

    return amount > 0 ? amount * count : count;
};

const displayAmountForSelectedDate = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit) return 0;

    const effectiveVersion = effectiveVersionForSelectedDate.value;

    // If we have version history but the selected date falls outside every
    // version's effective window, there is no value to redeem against.
    if (benefit.version_history?.length > 0 && !effectiveVersion) {
        return 0;
    }

    const valueDetails =
        effectiveVersion?.value_details || benefit.benefit.value_details || {};
    const count = parseFloat(valueDetails.count) || 1;
    const baseAmount = effectiveVersion
        ? parseFloat(effectiveVersion.amount) || 0
        : parseFloat(benefit.benefit.amount) || 0;
    const periodType =
        effectiveVersion?.period_type || benefit.benefit.period_type;
    const isCountBased = benefit.benefit.values_summary?.is_count_based;

    // For monthly benefits, check if selected date's month matches override month
    if (periodType?.toLowerCase() === 'monthly') {
        const overrideMonth = valueDetails.override_month?.month;
        const overrideValue = valueDetails.override_month?.value;

        if (
            overrideMonth &&
            overrideValue != null &&
            usageFormData.value.date
        ) {
            // Use dayjs to avoid timezone issues when extracting month
            const selectedMonth = String(
                dayjs(usageFormData.value.date).month() + 1
            ).padStart(2, '0');

            if (overrideMonth === selectedMonth) {
                return (parseFloat(overrideValue) || 0) * count;
            }
        }

        // Fall back to base amount for non-override months, applying the count
        // multiplier (or count itself for $0 count benefits) when count-based.
        return applyCountMultiplier(baseAmount, count, isCountBased);
    }

    // For non-monthly benefits, use the effective version's amount when known
    // (covers inactive benefits) and fall back to the regular display amount
    // calculation when no version history is available. Either way the count
    // multiplier must be applied so count-based benefits match the backend limit.
    return effectiveVersion
        ? applyCountMultiplier(baseAmount, count, isCountBased)
        : calculateDisplayAmount(benefit);
});

const calculatedUsagePercentage = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit) {
        return 0;
    }

    let maxValue;
    if (isCountBased.value) {
        maxValue =
            benefit?.benefit?.value_details?.count || displayAmount.value || 0;
    } else {
        maxValue = displayAmount.value;
    }
    const usedValue = effectiveCurrentPeriodUsage.value || 0;
    return maxValue > 0
        ? Math.min(100, Math.round((usedValue / maxValue) * 100))
        : 0;
});

const handleRowClick = () => {
    // Don't open sidebar for locked benefits
    if (props.benefitItem.isLocked) {
        return;
    }
    openBenefitDetails(props.benefitItem);
};

// Usage form data
const usageFormData = ref({
    amount: 0,
    date: dayjs().format('YYYY-MM-DD'),
    count: 0,
});

// Calculate usage for the selected period
const usageForSelectedPeriod = computed(() => {
    if (!usageFormData.value.date || !liveBenefit.value) return 0;

    const periodType = liveBenefit.value.benefit?.period_type;
    const selectedPeriodKey = store.getPeriodKey(
        usageFormData.value.date,
        periodType
    );
    const currentPeriodKey = store.getPeriodKey(new Date(), periodType);

    // If selected period is current period, use current_period_usage
    if (selectedPeriodKey === currentPeriodKey) {
        return liveBenefit.value.current_period_usage || 0;
    }

    // Otherwise, calculate usage from usage_records for that period
    const usageRecords = liveBenefit.value.usage_records || [];
    return usageRecords
        .filter((record) => {
            const recordPeriodKey = store.getPeriodKey(
                record.used_at,
                periodType
            );
            return recordPeriodKey === selectedPeriodKey;
        })
        .reduce(
            (sum, record) =>
                sum +
                (isNcred.value
                    ? parseFloat(record.usage_count) || 0
                    : parseFloat(record.total_amount_used) || 0),
            0
        );
});

// Count used within the selected date's period — mirrors usageForSelectedPeriod
// but sums usage_count, so "uses remaining" for count-based benefits is scoped
// to the period of the date being logged rather than always the current period
// (parallels how remainingAmount uses usageForSelectedPeriod).
const countUsedForSelectedPeriod = computed(() => {
    if (!usageFormData.value.date || !liveBenefit.value) return 0;

    const periodType = liveBenefit.value.benefit?.period_type;
    const selectedPeriodKey = store.getPeriodKey(
        usageFormData.value.date,
        periodType
    );
    const currentPeriodKey = store.getPeriodKey(new Date(), periodType);

    const recordsInPeriod = (liveBenefit.value.usage_records || []).filter(
        (record) =>
            store.getPeriodKey(record.used_at, periodType) === selectedPeriodKey
    );

    // By-count benefits with a dollar value (e.g. 2 × $250): the user enters a
    // $ amount, so each usage record represents exactly one use — count the
    // records. Summing usage_count would under-count historical periods, since
    // records logged before the benefit became by-count carry usage_count = 0.
    if (isByCountValueBenefit.value) {
        return recordsInPeriod.length;
    }

    // Pure count-based benefits can log multiple uses per record, so sum
    // usage_count. Use the recalculated count for the current period (handles
    // anniversary reset basis + optimistic updates); other periods sum records.
    if (selectedPeriodKey === currentPeriodKey) {
        return (
            store.recalculateCurrentPeriodUsage(liveBenefit.value)
                .count_used_current_period || 0
        );
    }
    return recordsInPeriod.reduce(
        (sum, record) => sum + (parseFloat(record.usage_count) || 0),
        0
    );
});

// Computed properties for validation
const remainingAmount = computed(() => {
    const benefitAmount = displayAmountForSelectedDate.value;
    const usedAmount = usageForSelectedPeriod.value;
    return Math.max(0, benefitAmount - usedAmount).toFixed(2);
});

// Per-transaction cap for by-count benefits (value_details.count > 1): a
// single entry can't exceed the version's per-use amount even when the
// period still has room (e.g. a 2 × $250 credit caps each entry at $250
// though $500 remains). Mirrors the backend
// ValidateBenefitUsagePerTransactionAmount rule. displayAmountForSelectedDate
// is the period total here, so dividing by count yields the per-use amount
// with the same monthly override-month handling. Infinity (no cap) for
// single-allotment benefits.
const perTransactionMaxAmount = computed(() => {
    const count =
        parseFloat(liveBenefit.value?.benefit?.value_details?.count) || 1;
    if (count <= 1) return Infinity;
    return (parseFloat(displayAmountForSelectedDate.value) || 0) / count;
});

// Largest amount the "Max" pill can fill on the regular usage form: the
// period's remaining value, capped by the per-transaction limit for by-count
// benefits (mirrors the effectiveMaxAmount enforced in validate()).
const maxFillableAmount = computed(() =>
    Math.min(parseFloat(remainingAmount.value), perTransactionMaxAmount.value)
);

const isBenefitFullyRedeemedThisYear = computed(() => {
    return store.isBenefitFullyRedeemedThisYear(props.benefitItem);
});

const showFullyRedeemedIcon = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit) return false;

    // Use recalculated current period usage instead of stale backend value
    const usage = currentPeriodUsage.value || 0;

    // Get period limit
    let periodLimit;
    if (isCountBased.value) {
        periodLimit =
            parseFloat(benefit?.benefit?.value_details?.count) ||
            displayAmount.value ||
            0;
    } else {
        periodLimit = displayAmount.value;
    }

    return periodLimit > 0 && usage >= periodLimit;
});

// Calculate current period usage on frontend to handle multi-year benefits correctly
// Multi-year benefits should only show usage if it was used this year
const currentPeriodUsage = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit) return 0;

    const recalculated = store.recalculateCurrentPeriodUsage(benefit);
    return recalculated.current_period_usage || 0;
});

const validate = () => {
    formErrors.value = {};

    // Block usage entry when version history exists and the selected date
    // falls outside every version's effective window.
    if (
        liveBenefit.value?.version_history?.length > 0 &&
        !hasActiveVersionForSelectedDate.value
    ) {
        formErrors.value.usageDate =
            'No benefit version was active on this date';
        return false;
    }

    if (!isCountBased.value) {
        if (!usageFormData.value.amount) {
            formErrors.value.usageAmount = 'Enter amount';
        }
        if (!usageFormData.value.date) {
            formErrors.value.usageDate = 'Enter date';
        }
        const effectiveMaxAmount = Math.min(
            parseFloat(remainingAmount.value),
            perTransactionMaxAmount.value
        );
        if (usageFormData.value.amount > effectiveMaxAmount) {
            formErrors.value.usageAmount = `Max amount is ${effectiveMaxAmount.toFixed(2)}`;
            if (props.benefitItem?.benefit?.value_details?.count > 1) {
                formErrors.value.usageAmount += ` per use`;
            }
        }
        // By-count benefits are also capped on the number of uses per period
        // (mirrors backend ValidateBenefitUsageCount). Each amount entry counts
        // as one use, so block once the period's uses are exhausted.
        if (
            isByCountValueBenefit.value &&
            parseFloat(remainingCount.value) < 1
        ) {
            formErrors.value.usageAmount = 'No remaining uses this period';
        }
    } else {
        if (usageFormData.value.count > parseFloat(remainingCount.value)) {
            formErrors.value.usageCount = `Max uses is ${remainingCount.value}`;
        }

        if (!usageFormData.value.count) {
            formErrors.value.usageCount = 'Enter count';
        }
    }

    return Object.keys(formErrors.value).length === 0;
};

// Handler method for usage submission
const handleUsageSubmit = async (closePopover) => {
    if (!validate()) {
        return;
    }

    const success = await store.handleUsageSubmit(
        usageFormData.value,
        props.benefitItem
    );

    if (success) {
        usageFormData.value = {
            amount: '',
            count: null,
            date: dayjs().format('YYYY-MM-DD'),
        };
        closePopover();
        emit('add-usage', props.benefitItem);
    }
};

const cdnUrl = computed(() => {
    return store.cdnUrl;
});

// Open upgrade modal for locked benefits
const openUpgradeModal = () => {
    store.setShowUpgradeModal(true);
};

watch(
    () => usageFormData.value,
    () => {
        formErrors.value = {};
    },
    { deep: true }
);
</script>
